import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PlusIcon, TrashIcon } from '@/components/icons';
import type { MCPInstanceFormState } from '@/pages/mcp/mcp-instance-form.utils';

interface MCPInstanceFormProps {
  form: MCPInstanceFormState;
  onChange: (next: MCPInstanceFormState) => void;
}

export function MCPInstanceForm({ form, onChange }: MCPInstanceFormProps) {
  const setField = <K extends keyof MCPInstanceFormState>(key: K, value: MCPInstanceFormState[K]) =>
    onChange({ ...form, [key]: value });

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-1 self-start">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">基本信息</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">实例名称用于在列表中识别这个 MCP。</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">名称</label>
            <Input
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="如: my-search"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">描述</label>
            <Textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">连接配置</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            指定 MCP 服务地址、超时与 Headers，Agent 调用时按这些配置连接。
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Server URL</label>
            <Input
              value={form.serverUrl}
              onChange={(e) => setField('serverUrl', e.target.value)}
              placeholder="https://..."
            />
            <p className="text-xs text-muted-foreground">
              传输方式默认 Streamable HTTP，绝大多数远端 MCP 走此协议。
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">超时（秒）</label>
              <Input
                type="number"
                min={0}
                value={form.timeoutSeconds || ''}
                onChange={(e) =>
                  setField(
                    'timeoutSeconds',
                    e.target.value === '' ? 0 : Math.max(0, Number(e.target.value))
                  )
                }
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">0 表示使用平台默认超时。</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">TLS 校验</label>
              <label className="flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.skipTlsVerify}
                  onChange={(e) => setField('skipTlsVerify', e.target.checked)}
                  className="h-4 w-4"
                />
                <span className="text-sm">跳过 TLS 证书校验</span>
              </label>
              <p className="text-xs text-muted-foreground">
                仅在自签证书或内网调试时开启，生产环境请保持关闭。
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium">Headers</label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  HTTP 请求头，常见为认证用的 Authorization。
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => setField('headers', [...form.headers, { key: '', value: '' }])}
              >
                <PlusIcon className="h-3 w-3 mr-1" />
                添加
              </Button>
            </div>
            {form.headers.length === 0 ? (
              <div className="rounded-md border border-dashed bg-muted/20 px-4 py-6 text-center">
                <p className="text-sm text-muted-foreground">暂无 header</p>
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium w-1/3">名称</th>
                      <th className="px-3 py-2 text-left font-medium">值</th>
                      <th className="w-9" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {form.headers.map((h, i) => (
                      <tr key={i} className="group hover:bg-muted/20">
                        <td className="px-2 py-1.5">
                          <Input
                            placeholder="Header 名"
                            value={h.key}
                            onChange={(e) => {
                              const next = [...form.headers];
                              next[i] = { ...next[i], key: e.target.value };
                              setField('headers', next);
                            }}
                            className="h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-2"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            placeholder="值"
                            value={h.value}
                            onChange={(e) => {
                              const next = [...form.headers];
                              next[i] = { ...next[i], value: e.target.value };
                              setField('headers', next);
                            }}
                            className="h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-2 font-mono"
                          />
                        </td>
                        <td className="px-1 py-1.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              const next = [...form.headers];
                              next.splice(i, 1);
                              setField('headers', next);
                            }}
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
